import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {IKnowledgeBaseItem} from 'src/app/interfaces/general/knowledge-base/knowledge-base-item.interface';
import {KnowledgeBaseService} from 'src/app/modules/knowledge-base/knowledge-base.service';
import {GetAllAnswers, GetAnswer} from 'src/app/store/knowledge-base/kb-items.actions';
import {firstValueFrom} from 'rxjs';
import {errorHandler} from 'src/app/shared/helpers/error-handler';
import {AppInsightsService} from 'src/app/core/app-insights/app-insights.service';

export interface IKbItemsState {
    data: IKnowledgeBaseItem[];
    hasValue: boolean;
    loading: boolean;
    error: any;
}

@State<IKbItemsState>({
    name: 'kbItems',
    defaults: {
        data: [],
        hasValue: false,
        loading: false,
        error: null,
    },
})
@Injectable()
export class KnowledgeBaseItemsState {
    constructor(
        private kbService: KnowledgeBaseService,
        private insights: AppInsightsService,
    ) { }

    @Selector()
    public static answers(state: IKbItemsState): IKnowledgeBaseItem[] {
        return state.data;
    }

    @Selector()
    public static loading(state: IKbItemsState): boolean {
        return state.loading;
    }

    @Selector()
    public static error(state: IKbItemsState): any {
        return state.error;
    }

    @Action(GetAllAnswers)
    public async getAllAnswers(ctx: StateContext<IKbItemsState>): Promise<void> {
        ctx.patchState({loading: true, error: null});
        try {
            const kbItems: IKnowledgeBaseItem[] = await firstValueFrom(this.kbService.getAll());
            ctx.patchState({
                data: kbItems,
                hasValue: !!kbItems?.length,
                loading: false,
                error: null,
            });
        } catch (error) {
            errorHandler({
                error,
                appInsightsSrv: this.insights,
                scope: 'KnowledgeBaseItemsState'
            });
            ctx.patchState({
                data: [],
                hasValue: false,
                loading: false,
                error: error.message ?? error,
            });
        }
    }

    @Action(GetAnswer)
    public async getAnswer(ctx: StateContext<IKbItemsState>, {question}: GetAnswer): Promise<void> {
        ctx.patchState({loading: true});
        try {
            const kbItem: IKnowledgeBaseItem = await firstValueFrom(
                this.kbService.getAnswer(question)
            );
            ctx.patchState({
                data: [kbItem],
                hasValue: !!kbItem,
                loading: false,
                error: null,
            });
        } catch (error) {
            ctx.patchState({
                data: null,
                hasValue: false,
                loading: false,
                error: error.message ?? error,
            });
        }
    }
}
